/* eslint-disable indent */
import { createExportHandlers } from '@src/main/modules/messages/utils/createExportHandlers';
import { normalizePosition } from '@src/main/modules/messages/utils/formatters';
import { Position } from '@src/main/modules/parameters/domain/fleet-positions/position.interface';
import PositionForm from '@src/main/modules/parameters/ui/fleet-positions/components/positions/PositionForm';
import { useCreatePosition } from '@src/main/modules/parameters/ui/fleet-positions/hooks/useCreatePosition';
import { useDeletePosition } from '@src/main/modules/parameters/ui/fleet-positions/hooks/useDeletePosition';
import { usePositions } from '@src/main/modules/parameters/ui/fleet-positions/hooks/usePositions';
import { useUpdatePosition } from '@src/main/modules/parameters/ui/fleet-positions/hooks/useUpdatePosition';
import { getPositionSchema } from '@src/main/modules/parameters/ui/fleet-positions/positionSchema';
import { PERMISSIONS } from '@src/main/shared/constants/permissions';
import { useAuth } from '@src/main/shared/context/AuthContext';
import { useToast } from '@src/main/shared/context/ToastContext';
import AppDialog from '@src/main/shared/ui/components/dialog/AppDialog';
import ConfirmDeleteDialog from '@src/main/shared/ui/components/dialog/ConfirmDeleteDialog';
import HeaderTitle from '@src/main/shared/ui/components/header/HeaderTitle';
import AppLoading from '@src/main/shared/ui/components/spinner/AppLoading';
import AppTable from '@src/main/shared/ui/components/table/AppTable';
import TableHeader from '@src/main/shared/ui/components/table/TableHeader';
import { formatList } from '@src/main/shared/utils/formatList';
import { showErrorMessage, showSuccessMessage } from '@src/main/shared/utils/toastUtils';
import { useQueryClient } from '@tanstack/react-query';
import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

interface PositionRow extends Position {
  id: string;
}

const PositionsSection: FC = () => {
  const { t } = useTranslation();
  const { hasPermission } = useAuth();
  const hasEditPermissions = hasPermission(PERMISSIONS.PARAMETERS.POSITIONS_FLEETS.edit);

  const [isRefreshing, setIsRefreshing] = useState(false);
  const [selectedPositions, setSelectedPositions] = useState<PositionRow[]>([]);
  const { data = [], isLoading: loadingPositions } = usePositions();

  const queryClient = useQueryClient();
  const toast = useToast();

  const { mutateAsync: createPosition } = useCreatePosition();
  const { mutateAsync: updatePosition } = useUpdatePosition();
  const { mutateAsync: deletePosition } = useDeletePosition();

  const [isModalVisible, setIsModalVisible] = useState(false);
  const [newEditPosition, setNewEditPosition] = useState<Position>({
    positionId: '',
    position: '',
    description: '',
  });

  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const [positionToDelete, setPositionToDelete] = useState<Position | null>(null);
  const [isBulkDelete, setIsBulkDelete] = useState(false);

  const toolbarVisibility = [
    true,
    hasEditPermissions,
    selectedPositions.length > 0,
    true,
    false,
    false,
  ];

  const positionsData: PositionRow[] = data.map((p) => ({
    id: p.positionId!,
    ...p,
  }));

  const FILE_NAME = t('parameters.fleetsAndPositions.positions.fileName');
  const { onExportExcel, onExportPdf } = createExportHandlers(
    data,
    normalizePosition,
    FILE_NAME,
    t,
  );

  const handleShowCreatePosition = () => {
    setNewEditPosition({
      positionId: '',
      position: '',
      description: '',
    });
    setValidationErrors({});
    setIsModalVisible(true);
  };

  const handleCreateEdit = async () => {
    const schema = getPositionSchema(t);

    try {
      schema.parse(newEditPosition);

      if (newEditPosition.positionId) {
        await updatePosition({
          id: newEditPosition.positionId,
          data: {
            position: newEditPosition.position,
            description: newEditPosition.description,
          },
        });
        showSuccessMessage(toast, t('parameters.fleetsAndPositions.positions.updatedSuccess'));
      } else {
        await createPosition({
          position: newEditPosition.position,
          description: newEditPosition.description,
        });
        showSuccessMessage(toast, t('parameters.fleetsAndPositions.positions.createdSuccess'));
      }

      await queryClient.invalidateQueries({ queryKey: ['position'] });
      setIsModalVisible(false);
      setValidationErrors({});
    } catch (error) {
      if (error instanceof z.ZodError) {
        const errors: Record<string, string> = {};
        error.errors.forEach((err) => {
          if (err.path.length > 0) {
            const key = err.path[0];
            errors[key] = err.message;
          }
        });
        setValidationErrors(errors);
      } else {
        showErrorMessage(toast, error);
      }
    }
  };

  const handleEditPosition = (row: PositionRow) => {
    setNewEditPosition({
      positionId: row.positionId,
      position: row.position,
      description: row.description,
    });
    setValidationErrors({});
    setIsModalVisible(true);
  };

  const handleChange = (field: keyof Position, value: string) => {
    setNewEditPosition((prev) => ({ ...prev, [field]: value }));
  };

  const handleDeletePosition = (row: PositionRow) => {
    const position: Position = {
      positionId: row.positionId,
      position: row.position,
      description: row.description,
    };
    setPositionToDelete(position);
  };

  const deleteSelectedPositions = () => {
    if (selectedPositions.length === 0) return;
    setIsBulkDelete(true);
  };

  const confirmDelete = async () => {
    try {
      if (isBulkDelete) {
        await Promise.all(
          selectedPositions.map((position) => deletePosition(position.positionId!)),
        );
        setSelectedPositions([]);
      } else if (positionToDelete) {
        await deletePosition(positionToDelete.positionId!);
        setSelectedPositions((prev) =>
          prev.filter((f) => f.positionId !== positionToDelete.positionId),
        );
      }

      await queryClient.invalidateQueries({ queryKey: ['position'] });
      showSuccessMessage(toast, t('parameters.fleetsAndPositions.positions.deletedSuccess'));
    } catch (error) {
      showErrorMessage(toast, error);
    } finally {
      setPositionToDelete(null);
      setIsBulkDelete(false);
    }
  };

  const rejectDelete = () => {
    setPositionToDelete(null);
    setIsBulkDelete(false);
  };

  return (
    <div className="flex flex-col gap-2">
      <HeaderTitle title={t('parameters.fleetsAndPositions.positions.title')} />
      <TableHeader
        onCreate={handleShowCreatePosition}
        onDelete={deleteSelectedPositions}
        onRefresh={async () => {
          setIsRefreshing(true);
          setTimeout(() => setIsRefreshing(false), 500);
        }}
        onExportExcel={onExportExcel}
        onExportPdf={onExportPdf}
        visibility={toolbarVisibility}
        exportable
      />
      <AppLoading isLoading={loadingPositions || isRefreshing}>
        <AppTable
          headers={[
            { name: t('parameters.fleetsAndPositions.positions.position'), field: 'position' },
            {
              name: t('parameters.fleetsAndPositions.positions.description'),
              field: 'description',
            },
          ]}
          data={positionsData}
          onEdit={hasEditPermissions ? handleEditPosition : undefined}
          onDelete={hasEditPermissions ? handleDeletePosition : undefined}
          onSelectedChange={hasEditPermissions ? setSelectedPositions : undefined}
        />
      </AppLoading>
      <AppDialog
        visible={isModalVisible}
        title={
          newEditPosition.positionId
            ? t('parameters.fleetsAndPositions.positions.updatePosition', {
                position: newEditPosition.position,
              })
            : t('parameters.fleetsAndPositions.positions.createPosition')
        }
        onClose={() => setIsModalVisible(false)}
        disableButtons={loadingPositions}
        onConfirm={handleCreateEdit}
      >
        <PositionForm
          values={newEditPosition}
          handleChange={handleChange}
          errors={validationErrors}
        />
      </AppDialog>
      <ConfirmDeleteDialog
        confirmDelete={confirmDelete}
        rejectDelete={rejectDelete}
        visible={isBulkDelete || positionToDelete !== null}
        message={
          isBulkDelete
            ? t('parameters.fleetsAndPositions.positions.confirmBulkDelete', {
                positions: formatList(
                  selectedPositions.map((f) => f.position),
                  t('common.and'),
                ),
              })
            : t('parameters.fleetsAndPositions.positions.confirmDelete', {
                position: positionToDelete?.position,
              })
        }
      />
    </div>
  );
};

export default PositionsSection;
